/**
 * PDF exports (R3 T2): the latest PDF per document, while it is made and once
 * it is ready or failed.
 *
 * A PDF is always converted from a Word version of the document (never
 * re-rendered), so it can take a while — the converter runs in the
 * background and this store polls the PDF version until it settles:
 *
 *   exportPdf       a response document's PDF (Export menu → PDF). Resolves
 *                   when the PDF is ready and its download has started; throws
 *                   the export's error (incl. the template check's 409, D4) or
 *                   the PDF's own failure message.
 *   retryPdf        a failed PDF again, from the SAME Word file.
 *   previewFormPdf  a completed buyer form's "PDF preview" (T7 wires the
 *                   button): pass the `form_docx` id from the form export.
 *   downloadPdf     download the ready PDF again (fetches a fresh link).
 *
 * Entries are keyed by artifact id.
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension' // required for devtools typing
import {
  ApiRequestError,
  convertOutputToPdf,
  downloadFromUrl,
  exportArtifactToPdf,
  getOutputVersion,
  type ExportOptions,
  type PendingAiChangeNotice,
} from '@/lib/api'
import type { OutputVersionWithDownload } from '@/types/output-version'

// ===================
// Types
// ===================

export type PdfExportEntry =
  | { phase: 'converting' }
  | { phase: 'ready'; version: OutputVersionWithDownload }
  /** `parentId` is the Word version a retry converts again. */
  | { phase: 'failed'; error: string; parentId: string | null }

interface PdfExportState {
  entries: Record<string, PdfExportEntry>

  exportPdf: (
    artifactId: string,
    templateId?: string,
    options?: ExportOptions,
  ) => Promise<{ pendingAiChanges: PendingAiChangeNotice[] }>
  retryPdf: (artifactId: string) => Promise<void>
  previewFormPdf: (artifactId: string, formDocxOutputId: string) => Promise<OutputVersionWithDownload>
  downloadPdf: (artifactId: string) => Promise<void>
  clear: (artifactId: string) => void
  reset: () => void
}

// ===================
// Polling
// ===================

export const PDF_POLL_INTERVAL_MS = 2_000
export const PDF_POLL_TIMEOUT_MS = 3 * 60_000
const GENERIC_PDF_ERROR = "The PDF couldn't be made. Try again."
const PDF_TIMEOUT_ERROR = 'Making the PDF is taking longer than expected. Try again.'

/** Poll one PDF version until it is no longer pending. */
async function waitForPdf(artifactId: string, version: OutputVersionWithDownload): Promise<OutputVersionWithDownload> {
  let current = version
  const deadline = Date.now() + PDF_POLL_TIMEOUT_MS
  while (current.status === 'pending') {
    if (Date.now() >= deadline) {
      return { ...current, status: 'failed', error: PDF_TIMEOUT_ERROR, downloadUrl: null }
    }
    await new Promise((resolve) => setTimeout(resolve, PDF_POLL_INTERVAL_MS))
    current = await getOutputVersion(artifactId, current.id)
  }
  return current
}

function messageOf(err: unknown): string {
  return err instanceof Error && err.message ? err.message : GENERIC_PDF_ERROR
}

// ===================
// Store
// ===================

export const usePdfExportStore = create<PdfExportState>()(
  devtools(
    (set, get) => {
      const setEntry = (artifactId: string, entry: PdfExportEntry | null) =>
        set((state) => {
          const entries = { ...state.entries }
          if (entry) entries[artifactId] = entry
          else delete entries[artifactId]
          return { entries }
        })

      /** Poll to the end and record it; returns the settled version. */
      const settle = async (
        artifactId: string,
        version: OutputVersionWithDownload,
        filename?: string,
        { download }: { download: boolean } = { download: true },
      ): Promise<OutputVersionWithDownload> => {
        const settled = await waitForPdf(artifactId, version)
        if (settled.status === 'ready' && settled.downloadUrl) {
          setEntry(artifactId, { phase: 'ready', version: settled })
          if (download) downloadFromUrl(settled.downloadUrl, filename)
        } else {
          setEntry(artifactId, { phase: 'failed', error: settled.error ?? GENERIC_PDF_ERROR, parentId: settled.parentId })
        }
        return settled
      }

      const failedError = (settled: OutputVersionWithDownload) =>
        new ApiRequestError({ code: 'PDF_FAILED', message: settled.error ?? GENERIC_PDF_ERROR })

      return {
        entries: {},

        exportPdf: async (artifactId, templateId, options = {}) => {
          // The previous result goes; "Making PDF…" waits until the server
          // accepts, so a refused export (D4's warning) never flashes it.
          setEntry(artifactId, null)
          let started: Awaited<ReturnType<typeof exportArtifactToPdf>>
          try {
            started = await exportArtifactToPdf(artifactId, templateId, options, () =>
              setEntry(artifactId, { phase: 'converting' }),
            )
          } catch (err) {
            // Nothing was made: the caller shows the error (or the template
            // warning, D4), so the menu line goes back to empty.
            setEntry(artifactId, null)
            throw err
          }
          let settled: OutputVersionWithDownload
          try {
            settled = await settle(artifactId, started.pdf, started.filename)
          } catch (err) {
            setEntry(artifactId, { phase: 'failed', error: messageOf(err), parentId: started.pdf.parentId })
            throw err
          }
          if (settled.status !== 'ready') throw failedError(settled)
          return { pendingAiChanges: started.pendingAiChanges }
        },

        retryPdf: async (artifactId) => {
          const entry = get().entries[artifactId]
          if (entry?.phase !== 'failed' || !entry.parentId) return
          const { parentId } = entry
          setEntry(artifactId, { phase: 'converting' })
          try {
            await settle(artifactId, await convertOutputToPdf(artifactId, parentId))
          } catch (err) {
            setEntry(artifactId, { phase: 'failed', error: messageOf(err), parentId })
          }
        },

        previewFormPdf: async (artifactId, formDocxOutputId) => {
          setEntry(artifactId, { phase: 'converting' })
          let settled: OutputVersionWithDownload
          try {
            settled = await settle(artifactId, await convertOutputToPdf(artifactId, formDocxOutputId), undefined, {
              download: false,
            })
          } catch (err) {
            setEntry(artifactId, { phase: 'failed', error: messageOf(err), parentId: formDocxOutputId })
            throw err
          }
          if (settled.status !== 'ready') throw failedError(settled)
          return settled
        },

        downloadPdf: async (artifactId) => {
          const entry = get().entries[artifactId]
          if (entry?.phase !== 'ready') return
          // Links last five minutes; ask for a fresh one.
          const fresh = await getOutputVersion(artifactId, entry.version.id)
          if (fresh.status === 'ready' && fresh.downloadUrl) {
            setEntry(artifactId, { phase: 'ready', version: fresh })
            downloadFromUrl(fresh.downloadUrl)
          }
        },

        clear: (artifactId) => setEntry(artifactId, null),

        reset: () => set({ entries: {} }),
      }
    },
    { name: 'pdf-export-store' },
  ),
)
